import React, { useState, useEffect, useCallback } from 'react';
import { api } from '../../api/client';
import LoginTable from '../../components/LoginTable';
import NotificationPanel from '../../components/NotificationPanel';
import UserTable from '../../components/UserTable';
import PointsModal from '../../components/PointsModal';
import InviteCodePanel from '../../components/InviteCodePanel';
import OpeningHoursEditor from '../../components/OpeningHoursEditor';
import StrainManager from '../../components/StrainManager';
import SalesDashboard from '../../components/SalesDashboard';
import TickDashboard from '../../components/TickDashboard';
import ManualOrderPanel from '../../components/ManualOrderPanel';
import AdminOrderPanel from '../../components/AdminOrderPanel';
import UserAnalytics from '../../components/UserAnalytics';
import AchievementAdminPanel from '../../components/AchievementAdminPanel';
import TelegramSettingsPanel from '../../components/TelegramSettingsPanel';
import RewardConfigPanel from '../../components/RewardConfigPanel';
import WheelConfigPanel from '../../components/WheelConfigPanel';
import BackupPanel from '../../components/BackupPanel';
import GlobalGiftPanel from '../../components/GlobalGiftPanel';
import FeatureChecklist from '../../components/FeatureChecklist';
import CouponRewardPanel from '../../components/CouponRewardPanel';
import CouponMetricsPanel from '../../components/CouponMetricsPanel';
import NetReturnChart from '../../components/NetReturnChart';
import SalesProjectionChart from '../../components/SalesProjectionChart';
import AdminStockConsumption from '../../components/AdminStockConsumption';
import BatchDatePicker from '../../components/BatchDatePicker';

function StrainReelToggle() {
  const [enabled, setEnabled] = React.useState(null);
  const [saving, setSaving] = React.useState(false);

  React.useEffect(() => {
    api.get('/strains/settings').then(data => {
      setEnabled(data.enable_strain_reel);
    }).catch(() => {});
  }, []);

  const handleToggle = async () => {
    setSaving(true);
    try {
      const newVal = !enabled;
      await api.put('/admin/reward-config', {
        configs: [{ config_key: 'enable_strain_reel', config_value: String(newVal) }],
      });
      setEnabled(newVal);
    } catch {
      // ignore
    } finally {
      setSaving(false);
    }
  };

  if (enabled === null) return null;

  return (
    <div className="card" style={{ marginBottom: 20, display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 20px' }}>
      <div>
        <div style={{ fontWeight: 700, fontSize: 14 }}>Strain Photo Reel</div>
        <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>
          Show auto-rotating photo carousel on the Order page
        </div>
      </div>
      <button
        onClick={handleToggle}
        disabled={saving}
        style={{
          padding: '8px 18px', fontSize: 13, fontWeight: 600, border: 'none',
          borderRadius: 'var(--radius-sm)', cursor: saving ? 'wait' : 'pointer',
          background: enabled ? 'var(--success)' : 'var(--bg-secondary)',
          color: enabled ? '#fff' : 'var(--text-muted)',
          transition: 'all 0.2s',
        }}
      >
        {saving ? '...' : enabled ? 'ON' : 'OFF'}
      </button>
    </div>
  );
}

const STATUS_COLORS = {
  pending: 'badge-uncommon',
  completed: 'badge-success',
  cancelled: 'badge-redeemed',
  on_tick: 'badge-legendary',
};

const TAB_STYLE = (active) => ({
  fontWeight: active ? 700 : 400,
  color: active ? 'var(--accent)' : 'var(--text-secondary)',
  background: 'none',
  border: 'none',
  borderBottom: active ? '2px solid var(--accent)' : '2px solid transparent',
  cursor: 'pointer',
  transition: 'all 0.2s',
  whiteSpace: 'nowrap',
});

export default function AdminDashboard() {
  const [tab, setTab] = useState('dashboard');
  const [logins, setLogins] = useState([]);
  const [loginAttempts, setLoginAttempts] = useState([]);
  const [notifications, setNotifications] = useState({ notifications: [], unread_count: 0 });
  const [users, setUsers] = useState({ rows: [], total: 0 });
  const [query, setQuery] = useState({ page: 1, search: '', sort: 'created_at', order: 'desc' });
  const [pointsUser, setPointsUser] = useState(null);
  const [bookings, setBookings] = useState([]);
  const [tickKey, setTickKey] = useState(0);
  const [couponPicker, setCouponPicker] = useState(null);
  const [couponLoading, setCouponLoading] = useState(false);
  const [historyFilter, setHistoryFilter] = useState('all');
  const [historySearch, setHistorySearch] = useState('');
  const [removeConfirm, setRemoveConfirm] = useState(null);

  const fetchDashboardData = useCallback(() => {
    api.get('/admin/logins?limit=20').then(setLogins).catch(() => {});
    api.get('/admin/login-attempts?limit=50').then(setLoginAttempts).catch(() => {});
    api.get('/admin/notifications?limit=50').then(setNotifications).catch(() => {});
    api.get('/bookings/all').then(setBookings).catch(() => {});
  }, []);

  useEffect(() => {
    fetchDashboardData();
    const interval = setInterval(fetchDashboardData, 5000);
    return () => clearInterval(interval);
  }, [fetchDashboardData]);

  const fetchUsers = useCallback(() => {
    const params = new URLSearchParams({
      page: query.page,
      search: query.search,
      sort: query.sort,
      order: query.order,
    });
    api.get(`/users?${params}`).then(setUsers).catch(() => {});
  }, [query]);

  useEffect(() => { fetchUsers(); }, [fetchUsers]);

  const handleMarkRead = async (id) => {
    await api.put(`/admin/notifications/${id}/read`);
    setNotifications(prev => ({
      ...prev,
      unread_count: Math.max(0, prev.unread_count - 1),
      notifications: prev.notifications.map(n => n.id === id ? { ...n, read_at: new Date().toISOString() } : n),
    }));
  };

  const handleMarkAllRead = async () => {
    await api.put('/admin/notifications/read-all');
    setNotifications(prev => ({
      ...prev,
      unread_count: 0,
      notifications: prev.notifications.map(n => ({ ...n, read_at: n.read_at || new Date().toISOString() })),
    }));
  };

  const handleBookingStatus = async (id, status) => {
    try {
      await api.put(`/bookings/${id}/status`, { status });
      const updated = await api.get('/bookings/all');
      setBookings(updated);
      setTickKey(k => k + 1);
    } catch {
      // ignore
    }
  };

  const handleShowCoupons = async (bookingId) => {
    try {
      setCouponLoading(true);
      const coupons = await api.get(`/bookings/${bookingId}/matching-coupons`);
      setCouponPicker({ bookingId, coupons });
    } catch {
      // ignore
    } finally {
      setCouponLoading(false);
    }
  };

  const handleRedeemCoupon = async (bookingId, couponId) => {
    try {
      await api.put(`/bookings/${bookingId}/redeem-coupon`, { couponId });
      setCouponPicker(null);
      const updated = await api.get('/bookings/all');
      setBookings(updated);
      setTickKey(k => k + 1);
    } catch {
      // ignore
    }
  };

  const handleRemoveBooking = async (id) => {
    try {
      await api.post(`/bookings/${id}/remove`);
      setRemoveConfirm(null);
      const updated = await api.get('/bookings/all');
      setBookings(updated);
      setTickKey(k => k + 1);
    } catch (err) {
      alert(err.response?.data?.error || err.message);
    }
  };

  const pendingBookings = bookings.filter(b => b.status === 'pending');

  return (
    <div>
      <h1 style={{ marginBottom: 12 }}>Grow Room</h1>

      <div style={{
        display: 'flex', gap: 0, borderBottom: '1px solid var(--border)',
        marginBottom: 20, flexWrap: 'wrap'
      }}>
        <button className="admin-tab" style={TAB_STYLE(tab === 'dashboard')} onClick={() => setTab('dashboard')}>
          Dashboard
        </button>
        <button className="admin-tab" style={TAB_STYLE(tab === 'stock')} onClick={() => setTab('stock')}>
          Stock
        </button>
        <button className="admin-tab" style={TAB_STYLE(tab === 'sales')} onClick={() => setTab('sales')}>
          Sales
        </button>
        <button className="admin-tab" style={TAB_STYLE(tab === 'orders')} onClick={() => setTab('orders')}>
          Orders
          {pendingBookings.length > 0 && (
            <span className="badge badge-uncommon" style={{ marginLeft: 6, fontSize: 10 }}>{pendingBookings.length}</span>
          )}
        </button>
        <button className="admin-tab" style={TAB_STYLE(tab === 'users')} onClick={() => setTab('users')}>
          Users
        </button>
        <button className="admin-tab" style={TAB_STYLE(tab === 'settings')} onClick={() => setTab('settings')}>
          Settings
        </button>
        <button className="admin-tab" style={TAB_STYLE(tab === 'testing')} onClick={() => setTab('testing')}>
          Testing
        </button>
      </div>

      {tab === 'dashboard' && (
        <>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(320px, 100%), 1fr))', gap: 16, marginBottom: 20 }}>
            <LoginTable logins={logins} loginAttempts={loginAttempts} />
            <NotificationPanel
              notifications={notifications.notifications || []}
              unreadCount={notifications.unread_count || 0}
              onMarkRead={handleMarkRead}
              onMarkAllRead={handleMarkAllRead}
            />
          </div>

          <div style={{ marginBottom: 20 }}>
            <InviteCodePanel />
          </div>

          {/* Pending Orders Summary Card */}
          <div className="card" style={{ marginBottom: 20, padding: '16px 20px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <h3 style={{ margin: 0 }}>Pending Orders</h3>
              <span className={`badge ${pendingBookings.length > 0 ? 'badge-uncommon' : 'badge-success'}`} style={{ fontSize: 12 }}>
                {pendingBookings.length}
              </span>
            </div>
            <button
              onClick={() => setTab('orders')}
              style={{
                background: 'var(--accent)', color: '#fff', border: 'none', borderRadius: 'var(--radius-sm)',
                padding: '8px 16px', fontSize: 13, fontWeight: 600, cursor: 'pointer',
              }}
            >
              View Orders
            </button>
          </div>
        </>
      )}

      {tab === 'stock' && (
        <>
          <BatchDatePicker />

          <div style={{ marginBottom: 20 }}>
            <StrainManager />
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(320px, 100%), 1fr))', gap: 16, marginBottom: 20 }}>
            <ManualOrderPanel />
            <AdminOrderPanel />
            <AdminStockConsumption />
          </div>
        </>
      )}

      {tab === 'sales' && (
        <>
          <div style={{ marginBottom: 20 }}>
            <SalesDashboard />
          </div>

          <div style={{ marginBottom: 20 }}>
            <CouponMetricsPanel />
          </div>

          <div style={{ marginBottom: 20 }}>
            <TickDashboard key={tickKey} />
          </div>

          <div style={{ marginBottom: 20 }}>
            <SalesProjectionChart />
          </div>

          <div style={{ marginBottom: 20 }}>
            <NetReturnChart />
          </div>

          <div style={{ marginBottom: 20 }}>
            <NetReturnChart mode="current_batch" />
          </div>
        </>
      )}

      {tab === 'orders' && (() => {
        const filtered = bookings.filter(b => {
          if (historyFilter !== 'all' && b.status !== historyFilter) return false;
          if (historySearch && !b.username?.toLowerCase().includes(historySearch.toLowerCase())) return false;
          return true;
        }).sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
        return (
          <>
          {/* Pending & On Tick Bookings */}
          {pendingBookings.length > 0 && (
            <div className="card" style={{ marginBottom: 20 }}>
              <h3 style={{ marginBottom: 12 }}>
                Pending Pickups
                <span className="badge badge-uncommon" style={{ marginLeft: 8, fontSize: 11 }}>{pendingBookings.length}</span>
              </h3>
              <div style={{ overflowX: 'auto' }}>
                <table>
                  <thead>
                    <tr>
                      <th>User</th>
                      <th>Strain</th>
                      <th>Amount</th>
                      <th>Points</th>
                      <th>Pickup</th>
                      <th>Status</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {pendingBookings.map(b => (
                      <tr key={b.id}>
                        <td><strong>{b.username}</strong></td>
                        <td style={{ color: 'var(--text-secondary)' }}>{b.strain_name || '-'}</td>
                        <td>{b.amount}</td>
                        <td style={{ color: 'var(--warning)' }}>{b.points_awarded != null ? b.points_awarded : b.potential_points}</td>
                        <td style={{ fontSize: 13 }}>{new Date(b.pickup_time).toLocaleString()}</td>
                        <td><span className={`badge ${STATUS_COLORS[b.status]}`}>{b.status === 'on_tick' ? 'On Tick' : b.status}</span></td>
                        <td>
                          <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                            <button className="btn-success btn-small" onClick={() => handleBookingStatus(b.id, 'completed')}>
                              {b.status === 'on_tick' ? 'Paid' : 'Complete'}
                            </button>
                            {b.matching_coupons > 0 && (
                              <button
                                className="btn-small"
                                style={{ background: '#805ad5', color: '#e9d8fd', padding: '6px 10px', fontSize: 12 }}
                                onClick={() => handleShowCoupons(b.id)}
                                disabled={couponLoading}
                              >
                                Redeem Token
                              </button>
                            )}
                            {b.status === 'pending' && (
                              <button
                                className="btn-small"
                                style={{ background: '#c05621', color: '#fefcbf', padding: '6px 10px', fontSize: 12 }}
                                onClick={() => handleBookingStatus(b.id, 'on_tick')}
                              >
                                On Tick
                              </button>
                            )}
                            <button className="btn-danger btn-small" onClick={() => handleBookingStatus(b.id, 'cancelled')}>
                              Cancel
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          <div className="card">
            <h3 style={{ marginBottom: 12 }}>
              All Orders
              <span className="badge badge-uncommon" style={{ marginLeft: 8, fontSize: 11 }}>{filtered.length}</span>
            </h3>

            <div style={{ display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap' }}>
              <input
                type="text"
                placeholder="Search by username..."
                value={historySearch}
                onChange={e => setHistorySearch(e.target.value)}
                style={{ flex: 1, minWidth: 120 }}
              />
              <select value={historyFilter} onChange={e => setHistoryFilter(e.target.value)} style={{ minWidth: 140 }}>
                <option value="all">All Statuses</option>
                <option value="pending">Pending</option>
                <option value="completed">Completed</option>
                <option value="on_tick">On Tick</option>
                <option value="cancelled">Cancelled</option>
              </select>
            </div>

            {filtered.length === 0 ? (
              <div className="empty-state">No orders found</div>
            ) : (
              <div style={{ overflowX: 'auto' }}>
                <table>
                  <thead>
                    <tr>
                      <th>User</th>
                      <th>Strain</th>
                      <th>Amount</th>
                      <th>Points</th>
                      <th>Pickup</th>
                      <th>Status</th>
                      <th>Ordered</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filtered.map(b => (
                      <tr key={b.id}>
                        <td><strong>{b.username}</strong></td>
                        <td style={{ color: 'var(--text-secondary)' }}>{b.strain_name || '-'}</td>
                        <td>{b.amount}</td>
                        <td style={{ color: 'var(--warning)' }}>{b.points_awarded != null ? b.points_awarded : b.potential_points}</td>
                        <td style={{ fontSize: 13 }}>{new Date(b.pickup_time).toLocaleString()}</td>
                        <td>
                          <span className={`badge ${STATUS_COLORS[b.status]}`}>
                            {b.status === 'on_tick' ? 'On Tick' : b.status}
                          </span>
                        </td>
                        <td style={{ fontSize: 13, color: 'var(--text-muted)' }}>{new Date(b.created_at).toLocaleString()}</td>
                        <td>
                          <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                            {(b.status === 'pending' || b.status === 'on_tick') && (
                              <>
                                <button className="btn-success btn-small" onClick={() => handleBookingStatus(b.id, 'completed')}>
                                  {b.status === 'on_tick' ? 'Paid' : 'Complete'}
                                </button>
                                {b.status === 'pending' && (
                                  <button
                                    className="btn-small"
                                    style={{ background: '#c05621', color: '#fefcbf', padding: '6px 10px', fontSize: 12 }}
                                    onClick={() => handleBookingStatus(b.id, 'on_tick')}
                                  >
                                    On Tick
                                  </button>
                                )}
                                <button className="btn-danger btn-small" onClick={() => handleBookingStatus(b.id, 'cancelled')}>
                                  Cancel
                                </button>
                              </>
                            )}
                            {(b.status === 'completed' || b.status === 'cancelled') && (
                              <button
                                className="btn-small"
                                style={{ background: '#9b2c2c', color: '#fed7d7', padding: '6px 10px', fontSize: 12 }}
                                onClick={() => setRemoveConfirm(b)}
                              >
                                Remove
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
          </>
        );
      })()}

      {tab === 'users' && (
        <>
          <UserTable
            users={users}
            query={query}
            onQueryChange={setQuery}
            onAddPoints={(u) => setPointsUser(u)}
          />

          {pointsUser && (
            <PointsModal
              user={pointsUser}
              onClose={() => setPointsUser(null)}
              onDone={() => { setPointsUser(null); fetchUsers(); }}
            />
          )}

          <div style={{ marginTop: 20 }}>
            <UserAnalytics />
          </div>
        </>
      )}

      {tab === 'settings' && (
        <>
          <StrainReelToggle />
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(320px, 100%), 1fr))', gap: 16, marginBottom: 20 }}>
            <OpeningHoursEditor />
            <RewardConfigPanel />
            <TelegramSettingsPanel />
          </div>
          <div style={{ marginBottom: 20 }}>
            <WheelConfigPanel />
          </div>
          <div style={{ marginBottom: 20 }}>
            <CouponRewardPanel />
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(320px, 100%), 1fr))', gap: 16, marginBottom: 20 }}>
            <BackupPanel />
            <GlobalGiftPanel />
          </div>
          <div style={{ marginBottom: 20 }}>
            <AchievementAdminPanel />
          </div>
        </>
      )}

      {tab === 'testing' && (
        <FeatureChecklist />
      )}

      {/* Remove Order Confirmation Modal */}
      {removeConfirm && (
        <div className="modal-overlay" onClick={() => setRemoveConfirm(null)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <h3 style={{ marginBottom: 12, color: '#e53e3e' }}>Remove Order</h3>
            <div style={{ marginBottom: 16 }}>
              <p style={{ marginBottom: 8 }}>
                <strong>User:</strong> {removeConfirm.username}<br />
                <strong>Amount:</strong> {removeConfirm.amount} {removeConfirm.strain_name || ''}<br />
                <strong>Status:</strong> {removeConfirm.status}
              </p>
              <p style={{ color: '#e53e3e', fontSize: 13, fontWeight: 600 }}>
                This will permanently delete this order and reverse all effects (points, spins, XP, stock, achievements, coupons, level-ups).
              </p>
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              <button
                style={{
                  background: '#e53e3e', color: '#fff', border: 'none', borderRadius: 'var(--radius-sm)',
                  padding: '10px 16px', fontSize: 13, fontWeight: 700, cursor: 'pointer', flex: 1,
                }}
                onClick={() => handleRemoveBooking(removeConfirm.id)}
              >
                Yes, Remove Order
              </button>
              <button className="btn-secondary btn-small" style={{ flex: 1 }} onClick={() => setRemoveConfirm(null)}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Coupon Picker Modal */}
      {couponPicker && (
        <div className="modal-overlay" onClick={() => setCouponPicker(null)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <h3 style={{ marginBottom: 12 }}>Redeem Token</h3>
            <p style={{ color: 'var(--text-secondary)', fontSize: 13, marginBottom: 16 }}>
              Select a reward token to cover this booking. No points or spin tokens will be awarded.
            </p>
            {couponPicker.coupons.length === 0 ? (
              <div className="empty-state" style={{ padding: 20 }}>No matching coupons found</div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {couponPicker.coupons.map(c => (
                  <div key={c.id} style={{
                    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                    padding: '10px 12px', background: 'var(--bg-secondary)',
                    borderRadius: 'var(--radius-sm)', border: '1px solid var(--border)',
                  }}>
                    <div>
                      <div style={{ fontWeight: 700, fontSize: 14 }}>{c.coupon_code}</div>
                      <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>
                        {c.reward_detail} &middot; {new Date(c.created_at).toLocaleDateString()}
                      </div>
                    </div>
                    <button
                      className="btn-small"
                      style={{ background: '#805ad5', color: '#e9d8fd', padding: '6px 12px', fontSize: 12 }}
                      onClick={() => handleRedeemCoupon(couponPicker.bookingId, c.id)}
                    >
                      Redeem
                    </button>
                  </div>
                ))}
              </div>
            )}
            <div style={{ marginTop: 16 }}>
              <button className="btn-secondary btn-small" onClick={() => setCouponPicker(null)}>Cancel</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
